// Raised from 2 on 2026-07-17 (founder decision): a signed-in account must be
// a search upgrade over the anonymous per-IP allowance, not a downgrade.
export const FREE_SEARCH_QUOTA_DEFAULT = 15;
export const FREE_SEARCH_DUPLICATE_WINDOW_SECONDS = 15 * 60;
export const FIRM_MIN_SEATS = 2;
export const STARTER_CASE_LIMIT = 5;
export const STARTER_MONTHLY_PRICE_INR = 199;
export const STARTER_YEARLY_PRICE_INR = 899;

export const PROFESSIONAL_ANNUAL_PLANS = [
  { id: 'annual_5', name: 'Annual — 5 Cases', caseLimit: 5, priceYearlyInr: 2499 },
  { id: 'annual_10', name: 'Annual — 10 Cases', caseLimit: 10, priceYearlyInr: 3299 },
  { id: 'annual_25', name: 'Annual — 25 Cases', caseLimit: 25, priceYearlyInr: 4499 },
  { id: 'annual_50', name: 'Annual — 50 Cases', caseLimit: 50, priceYearlyInr: 5900 },
  { id: 'annual_100', name: 'Annual — 100 Cases', caseLimit: 100, priceYearlyInr: 7499 },
] as const;

export type ProfessionalAnnualPlanId = (typeof PROFESSIONAL_ANNUAL_PLANS)[number]['id'];

export function isProfessionalAnnualPlanId(value: string): value is ProfessionalAnnualPlanId {
  return PROFESSIONAL_ANNUAL_PLANS.some((plan) => plan.id === value);
}

export type PlanCode = 'starter' | 'lawyer_50' | 'lawyer_pro' | 'firm' | 'corporate' | 'annual';
// 'annual' is excluded from both: it's a checkout-only tier whose five SKUs
// don't fit the one-tier-one-case-limit shape of PLAN_LIMITS (different limits
// share the 'annual' tier). See caseLimitForPlanRecord for how its case
// limit is actually resolved (from the plan row's own case_limit column).
export type PublicPlanCode = Exclude<PlanCode, 'lawyer_50' | 'annual'>;
export type PaidPlanCode = Exclude<PlanCode, 'corporate' | 'annual'>;
export type BillingInterval = 'monthly' | 'yearly';

export interface PublicPlan {
  code: PublicPlanCode;
  name: string;
  audience: string;
  monthlyPriceInr: number | null;
  yearlyPriceInr: number | null;
  caseLimit: number | null;
  isWorkspacePlan: boolean;
  isRequestAccessOnly: boolean;
  seatMinimum?: number;
  isBestValue?: boolean;
  isYearlyOnly?: boolean;
  monthlyEquivalentInr?: number;
  features: string[];
}

export const PUBLIC_PLANS: PublicPlan[] = [
  {
    code: 'starter',
    name: 'Starter',
    audience: 'For individual litigants tracking their own court cases',
    monthlyPriceInr: STARTER_MONTHLY_PRICE_INR,
    yearlyPriceInr: STARTER_YEARLY_PRICE_INR,
    caseLimit: STARTER_CASE_LIMIT,
    isWorkspacePlan: false,
    isRequestAccessOnly: false,
    features: [
      `${STARTER_CASE_LIMIT} active monitored cases`,
      'Single personal account',
      'WhatsApp alerts',
      'Hearing and status updates',
      'Full order history and PDF downloads',
    ],
  },
  {
    code: 'lawyer_pro',
    name: 'Lawyer Pro',
    audience: 'Single-user plan for solo lawyers and individual practices',
    monthlyPriceInr: 999,
    yearlyPriceInr: 9990,
    caseLimit: 500,
    isWorkspacePlan: false,
    isRequestAccessOnly: false,
    isBestValue: true,
    features: [
      '500 active monitored cases',
      'Single user',
      'WhatsApp/email alerts',
      'Daily cause list',
      'Bulk CSV import',
      'Order archive',
      'Advanced filters',
      'Priority alerts',
    ],
  },
  {
    code: 'firm',
    name: 'Firm',
    audience: 'Multi-user workspace plan for firms and chambers',
    monthlyPriceInr: 799,
    yearlyPriceInr: 7990,
    caseLimit: 500,
    seatMinimum: FIRM_MIN_SEATS,
    isWorkspacePlan: true,
    isRequestAccessOnly: false,
    features: [
      '500 shared active monitored cases per workspace',
      `Minimum ${FIRM_MIN_SEATS} seats`,
      '₹799/month per seat',
      'Team workspace',
      'Role-based access',
      'Consolidated billing',
      'Shared case dashboard',
    ],
  },
  {
    code: 'corporate',
    name: 'Corporate',
    audience: 'For companies, banks, NBFCs, insurers and large legal teams',
    monthlyPriceInr: null,
    yearlyPriceInr: null,
    caseLimit: null,
    isWorkspacePlan: true,
    isRequestAccessOnly: true,
    features: [
      'Custom case volume',
      'API/custom workflow',
      'Onboarding and migration support',
      'Enterprise review and access controls',
    ],
  },
];

export const PLAN_LIMITS: Record<PaidPlanCode, number> = {
  starter: STARTER_CASE_LIMIT,
  lawyer_50: 50,
  lawyer_pro: 500,
  firm: 500,
};

export function planIdFor(code: PaidPlanCode, interval: BillingInterval): string {
  return `${code}_${interval}`;
}

export function planCodeFromPlanId(planId: string | null | undefined): PlanCode | null {
  if (!planId) return null;
  if (planId.startsWith('starter_')) return 'starter';
  if (planId.startsWith('lawyer_50_')) return 'lawyer_50';
  if (planId.startsWith('lawyer_pro_')) return 'lawyer_pro';
  if (planId.startsWith('firm_')) return 'firm';
  if (planId.startsWith('corporate_')) return 'corporate';
  if (planId.startsWith('annual_')) return 'annual';
  return null;
}

export function normalizePlanCode(value: string | null | undefined): PlanCode | null {
  if (!value) return null;
  if (['starter', 'lawyer_50', 'lawyer_pro', 'firm', 'corporate', 'annual'].includes(value)) {
    return value as PlanCode;
  }
  return null;
}

export function getCaseLimitForPlan(code: PlanCode | null | undefined): number {
  // 'annual' has no single case limit (all selectable volume plans share
  // the tier but differ in limit) — same "not defined here" sentinel as
  // corporate; callers must resolve it from the actual plan/subscription row
  // (see caseLimitForPlanRecord / the team-projection fallback in subscription.ts).
  if (!code || code === 'corporate' || code === 'annual') return 0;
  return PLAN_LIMITS[code];
}

export function caseLimitForPlanRecord(
  plan: { id?: string | null; tier?: string | null; case_limit?: number | string | null },
): number {
  if (plan.tier === 'starter' || plan.id === 'starter_monthly' || plan.id === 'starter_yearly') {
    return STARTER_CASE_LIMIT;
  }

  const value = Number(plan.case_limit || 0);
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

export function caseLimitForExistingSubscriptionRecord(
  plan: { id?: string | null; tier?: string | null; case_limit?: number | string | null },
  subscription: { case_limit?: number | string | null } | null | undefined,
): number {
  const storedLimit = Number(subscription?.case_limit || 0);
  if (Number.isFinite(storedLimit) && storedLimit > 0) return Math.floor(storedLimit);
  return caseLimitForPlanRecord(plan);
}

export function getRazorpayPlanEnvName(planId: string, currency: 'INR' | 'USD' = 'INR'): string | null {
  if (planId === 'starter_litigant_99_monthly') {
    return null;
  }
  if (currency === 'INR' && planId === 'starter_monthly') {
    return 'RAZORPAY_PLAN_STARTER_MONTHLY_199';
  }
  if (currency === 'INR' && planId === 'starter_yearly') {
    return 'RAZORPAY_PLAN_STARTER_YEARLY_899';
  }
  const code = planCodeFromPlanId(planId);
  const interval = planId.endsWith('_yearly') ? 'YEARLY' : planId.endsWith('_monthly') ? 'MONTHLY' : null;
  if (!code || !interval || code === 'corporate') return null;
  const suffix = currency === 'USD' ? '_USD' : '';
  return `RAZORPAY_PLAN_${code.toUpperCase()}_${interval}${suffix}`;
}

export function getConfiguredRazorpayPlanId(
  plan: { id?: string | null; razorpay_plan_id?: string | null; razorpay_plan_id_usd?: string | null },
  currency: 'INR' | 'USD' = 'INR',
): string | null {
  const envName = plan.id ? getRazorpayPlanEnvName(plan.id, currency) : null;
  const fromEnv = envName ? process.env[envName] || null : null;

  // Starter prices changed in July 2026. Never fall back to the old DB-backed
  // Razorpay price IDs, because those provider plans still charge ₹249/₹2,490.
  // The new amount-specific env vars must be configured before release.
  if (plan.id === 'starter_monthly' || plan.id === 'starter_yearly') {
    return fromEnv;
  }

  const fromDb = currency === 'USD' ? plan.razorpay_plan_id_usd : plan.razorpay_plan_id;
  return fromDb || fromEnv;
}
